/**
 * TimelineDrawer ("Intelligence Timeline")
 *
 * Side sheet showing the full audit event timeline for a taxonomy key —
 * both taxonomy and prompt operations (deploys, rollbacks, drafts, etc.).
 * Uses infinite scroll pagination — loads more events as the user scrolls down.
 */

import React, { useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../../../ui/sheet';
import { ScrollArea } from '../../../../ui/scroll-area';
import { useTimeline } from '../../hooks/useTaxonomy';

const EVENT_LABELS: Record<string, string> = {
  'taxonomy.draft.created': 'Taxonomy Draft Created',
  'taxonomy.draft.updated': 'Taxonomy Draft Updated',
  'taxonomy.deployed': 'Taxonomy Deployed',
  'taxonomy.rolled_back': 'Taxonomy Rolled Back',
  'taxonomy.archived': 'Taxonomy Archived',
  'taxonomy.draft.deleted': 'Taxonomy Draft Deleted',
  'prompt.draft.created': 'Prompt Draft Created',
  'prompt.draft.updated': 'Prompt Draft Updated',
  'prompt.deployed': 'Prompt Deployed',
  'prompt.rolled_back': 'Prompt Rolled Back',
  'prompt.archived': 'Prompt Archived',
  'prompt.draft.deleted': 'Prompt Draft Deleted',
};

const EVENT_DOTS: Record<string, string> = {
  'taxonomy.deployed': 'bg-green-500',
  'taxonomy.rolled_back': 'bg-amber-500',
  'taxonomy.archived': 'bg-gray-400',
  'taxonomy.draft.created': 'bg-blue-500',
  'taxonomy.draft.updated': 'bg-blue-400',
  'taxonomy.draft.deleted': 'bg-red-400',
  'prompt.deployed': 'bg-green-500',
  'prompt.rolled_back': 'bg-amber-500',
  'prompt.archived': 'bg-gray-400',
  'prompt.draft.created': 'bg-blue-500',
  'prompt.draft.updated': 'bg-blue-400',
  'prompt.draft.deleted': 'bg-red-400',
};

interface TimelineDrawerProps {
  taxonomyKey: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TimelineDrawer: React.FC<TimelineDrawerProps> = ({
  taxonomyKey,
  open,
  onOpenChange,
}) => {
  const {
    data,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useTimeline(taxonomyKey);

  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage || !open) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, open]);

  const events = data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[400px] sm:w-[440px]">
        <SheetHeader>
          <SheetTitle>Intelligence Timeline</SheetTitle>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-80px)] mt-4 px-4">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 bg-gray-100 animate-pulse rounded" />
              ))}
            </div>
          ) : events.length > 0 ? (
            <div className="space-y-3">
              {events.map((event) => (
                <div key={event.id} className="bg-white rounded-md border p-3">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${EVENT_DOTS[event.eventType] || 'bg-gray-400'}`} />
                      {EVENT_LABELS[event.eventType] || event.eventType}
                    </span>
                    <span className="text-xs text-gray-400">
                      v{event.entityVersion}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {event.actorEmail}
                  </p>
                  {event.note && (
                    <p className="text-xs text-gray-600 mt-1 italic">
                      "{event.note}"
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(event.createdAt).toLocaleString()}
                  </p>
                </div>
              ))}

              {/* Sentinel for infinite scroll */}
              <div ref={loadMoreRef} className="py-2 flex justify-center">
                {isFetchingNextPage && (
                  <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                )}
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No events yet.</p>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default TimelineDrawer;
